import { css } from '@emotion/react';
import { BREAKPOINTS, mediaQuery } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import type L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import { useMediaQuery } from 'react-responsive';
import { MAP_BREAKPOINT } from '../../styled';
import { ICON_FOR_MARKER_TYPE } from './icons';
import type { MapBounds, MapProps } from './types';

export const getBoundsFromMap = (map: L.Map): MapBounds | undefined => {
  const bounds = map.getBounds();
  if (!bounds) return;
  const formattedBounds: MapBounds = {
    northEast: bounds.getNorthEast(),
    southWest: bounds.getSouthWest(),
  };
  if (formattedBounds.northEast.lat === formattedBounds.southWest.lat) return;
  if (formattedBounds.northEast.lng === formattedBounds.southWest.lng) return;
  return formattedBounds;
};

const Map: FC<MapProps> = (props) => {
  const { zoom, center, markers, onLoad, forceCenter, onAutoPanEnd, currentMarkerPopup, ...rest } =
    props;
  const { translate } = useTranslation();

  const phoneBreakpoint = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.small}px)` });

  const MAP_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

  return (
    <MapContainer
      style={{ height: '100%', width: '100%' }}
      center={center}
      zoom={zoom}
      scrollWheelZoom
      css={css`
        .leaflet-control-container {
          height: 100%;
        }
        ${mediaQuery(MAP_BREAKPOINT)} {
          .leaflet-right {
            right: 10px;
          }
          .leaflet-top {
            top: 130px;
          }
        }
      `}
      zoomControl={false}
      {...rest}
    >
      <MapEffects {...props} />
      <MapEvents {...props} />
      <TileLayer url={MAP_URL} />
      {!phoneBreakpoint && <ZoomControl position="topright" />}
      {center && (
        <Marker position={center} icon={ICON_FOR_MARKER_TYPE['marker-red']}>
          <Popup>{translate('glossary.currentLocation')}</Popup>
        </Marker>
      )}
      {markers.map((marker, index) => {
        return (
          <Marker
            position={marker.position}
            icon={ICON_FOR_MARKER_TYPE[marker.type]}
            key={index}
            riseOnHover
            eventHandlers={{
              click: (event) => {
                marker.onClick?.();
                window.requestAnimationFrame(() => {
                  event.target.openPopup();
                });
              },
            }}
          >
            {currentMarkerPopup && <Popup>{currentMarkerPopup}</Popup>}
          </Marker>
        );
      })}
    </MapContainer>
  );
};

// eslint-disable-next-line import/no-default-export
export default Map;

const MapEvents: FC<MapProps> = (props) => {
  const { onMove, onZoomChange, onClick } = props;

  useMapEvents({
    click: (event) => {
      onClick?.(event);
    },
    zoomend: (event) => {
      const map = event.target as L.Map;
      onZoomChange?.(map.getZoom(), getBoundsFromMap(map));
    },
    dragend: (event) => {
      const map = event.target as L.Map;
      onMove?.(map.getCenter(), getBoundsFromMap(map));
    },
  });
  return null;
};

const MapEffects: FC<MapProps> = (props) => {
  const { zoom, center, forceCenter, onAutoPanEnd } = props;
  const map = useMap();

  useEffect(() => {
    if (map && center) {
      map.flyTo(center, zoom, { animate: false });
      window.requestAnimationFrame(() => {
        onAutoPanEnd?.(map.getCenter(), getBoundsFromMap(map));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  useEffect(() => {
    if (map && forceCenter) {
      map.panTo(forceCenter, { animate: false });
      onAutoPanEnd?.(map.getCenter(), getBoundsFromMap(map));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceCenter?.lat, forceCenter?.lng]);

  return null;
};
